import { useEffect, useState, useMemo } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Stack,
    HStack,
    SimpleGrid,
    IconButton,
    Divider,
    Text,
    Box,
    useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import LabUjiService from "services/report/ReportLabService.js";
import scheduleService from "services/report/scheduleService";

const emptyBaris = () => ({
    id_uji: "",
    kategori: "",
    nm_uji: "",
    result: "",
    satuan: "",
    methode: "",  
    lod_loq: "",   
    keterangan: "",
});

const emptyForm = () => ({
    upi_id: "",
    nm_upi: "",
    nm_jenis_uji: "",
    tgl_contoh: "",
    tgl_sur: "",
    tgl_lhu: "",
    tmp_uji: "",
    dokpath: "",
    created_by: localStorage.getItem("nip") || "",
    jenis_uji: [emptyBaris()],
});

export default function TambahPengujianModal({ isOpen, onClose, onCreated }) {
    const [form, setForm] = useState(emptyForm());
    const [selectedFile, setSelectedFile] = useState(null); // Menyimpan File Object secara lokal
    const [masterUji, setMasterUji] = useState([]);
    const [saving, setSaving] = useState(false);
    const toast = useToast();
    const [upiList, setUpiList] = useState([]);
    const [produkList, setProdukList] = useState([]);
    const [searchUpi, setSearchUpi] = useState("");
    const [searchProduk, setSearchProduk] = useState("");
    const [searchParam, setSearchParam] = useState("");

    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB

    const filteredUpiList = useMemo(() => {
        if (!searchUpi.trim()) return upiList;
        return upiList.filter((u) => u.UPI_NAMA && u.UPI_NAMA.toLowerCase().includes(searchUpi.toLowerCase()));
    }, [upiList, searchUpi]);

    const filteredProdukList = useMemo(() => {
        if (!searchProduk.trim()) return produkList;
        return produkList.filter((p) => p.nama_produk && p.nama_produk.toLowerCase().includes(searchProduk.toLowerCase()));
    }, [produkList, searchProduk]);

    const filteredMasterUji = useMemo(() => {
        if (!searchParam.trim()) return masterUji;
        const lower = searchParam.toLowerCase();
        return masterUji.filter((m) =>
            (m.parameter_uji && m.parameter_uji.toLowerCase().includes(lower)) ||
            (m.kategori && m.kategori.toLowerCase().includes(lower))
        );
    }, [masterUji, searchParam]);

    useEffect(() => {
        const fetchUpi = async () => {
            try {
                const res = await scheduleService.getUpi();
                setUpiList(res.data || []);
            } catch (err) {
                console.error("Gagal mengambil data UPI", err);
            }
        };
        fetchUpi();
    }, []);

    useEffect(() => {
        const fetchProduk = async () => {
            try {
                const res = await scheduleService.getProduk();
                setProdukList(res.data || []);
            } catch (err) {
                console.error("Gagal mengambil data Produk", err);
            }
        };
        fetchProduk();
    }, []);

    useEffect(() => {
        const fetchMasterUji = async () => {
            try {
                const res = await LabUjiService.getJenisUji();
                setMasterUji(res.data || []);
            } catch (err) {
                console.error("Gagal mengambil master uji", err);
            }
        };
        fetchMasterUji();
    }, []);

    const barisAnsIsCustom = (val) => {
        return val !== undefined && val !== null && val !== "";
    };

    // Hanya menyimpan objek file di state lokal (tidak langsung dikirim ke API)
    const handleFileSelect = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
        setSelectedFile(null);
        return;
    }

    // Maksimal 1 MB
    if (file.size > MAX_FILE_SIZE) {
        toast({
            title: "Ukuran file terlalu besar",
            description: "Ukuran dokumen maksimal 1 MB.",
            status: "error",
            duration: 4000,
            isClosable: true,
        });

        setSelectedFile(null);

        // Kosongkan kembali input file
        e.target.value = "";
        return;
    }

    setSelectedFile(file);
};

    const parseRequirementValue = (reqStr) => {
        if (!reqStr) return null;
        let clean = reqStr.toString().trim().toLowerCase();

        if (clean.includes("x10^") || clean.includes("*10^")) {
            const parts = clean.split(/x10\^|\*10\^/);
            const base = parseFloat(parts[0]) || 1;
            const exponent = parseFloat(parts[1]) || 0;
            return base * Math.pow(10, exponent);
        }

        if (clean.startsWith("<")) {
            const num = clean.replace("<", "").trim();
            return parseFloat(num);
        }

        const parsed = parseFloat(clean);
        return isNaN(parsed) ? null : parsed;
    };

    const evaluateKeterangan = (hasilUji, limitLoq) => {
        if (!hasilUji || !limitLoq) return "";

        const hasilStr = hasilUji.toString().trim().toLowerCase();
        const reqStr = limitLoq.toString().trim().toLowerCase();

        const validNotDetectedStrings = ["not detected", "nd", "tidak terdeteksi", "ttd", "nil"];
        if (validNotDetectedStrings.includes(hasilStr)) {
            return "Sesuai Standar";
        }

        if (reqStr === "negatif" || reqStr === "normal") {
            return hasilStr === reqStr ? "Sesuai Standar" : "Tidak Sesuai Standar";
        }

        const limitNum = parseRequirementValue(reqStr);
        const hasilNum = parseFloat(hasilStr);

        if (isNaN(hasilNum) || limitNum === null) {
            return "";
        }

        if (reqStr.startsWith("<")) {
            return hasilNum < limitNum ? "Sesuai Standar" : "Tidak Sesuai Standar";
        }

        return hasilNum <= limitNum ? "Sesuai Standar" : "Tidak Sesuai Standar";
    };

    const handleUpiChange = (e) => {
        const selectedId = e.target.value;
        const upi = upiList.find((u) => String(u.UPI_ID) === String(selectedId));
        if (!upi) return;

        setForm((prev) => ({
            ...prev,
            upi_id: String(upi.UPI_ID),
            nm_upi: upi.UPI_NAMA,
        }));
    };

    const handleProdukChange = (e) => {
        const selectedId = e.target.value;
        const prod = produkList.find((p) => String(p.id_produk) === String(selectedId));
        if (!prod) return;

        setForm((prev) => ({
            ...prev,
            nm_jenis_uji: prod.nama_produk,
        }));
    };

    const handleParamSelect = (idx, selectedId) => {
        if (!selectedId) return;

        const selectedMaster = masterUji.find(
            (m) => String(m.id_uji) === String(selectedId)
        );

        if (!selectedMaster) {
            console.warn("Master uji tidak ditemukan untuk ID:", selectedId);
            return;
        }

        const methodeVal = selectedMaster.methode || selectedMaster.metode || selectedMaster.method || "";
        const lodLoqVal = selectedMaster.requirement || selectedMaster.lod_loq || selectedMaster.lod || selectedMaster.loq || "";
        const nmUjiVal = selectedMaster.parameter_uji || selectedMaster.nm_uji || "";
        const satuanVal = selectedMaster.satuan || "%";
        const kategoriVal = selectedMaster.kategori || selectedMaster.kategori_uji || selectedMaster.nm_kategori || "";

        const barisSaatIni = form.jenis_uji[idx];
        const ket = evaluateKeterangan(barisSaatIni.result, lodLoqVal);

        const updatedJenisUji = [...form.jenis_uji];
        updatedJenisUji[idx] = {
            ...updatedJenisUji[idx],
            id_uji: Number(selectedMaster.id_uji),
            kategori: kategoriVal,
            nm_uji: nmUjiVal,
            satuan: satuanVal,
            methode: barisAnsIsCustom(barisSaatIni.methode) ? barisSaatIni.methode : methodeVal,
            lod_loq: barisAnsIsCustom(barisSaatIni.lod_loq) ? barisSaatIni.lod_loq : lodLoqVal,
            keterangan: ket,
        };

        setForm({ ...form, jenis_uji: updatedJenisUji });
    };

    const handleHasilChange = (idx, value) => {
        const updatedJenisUji = [...form.jenis_uji];
        const barisSaatIni = updatedJenisUji[idx];
        
        const ket = evaluateKeterangan(value, barisSaatIni.lod_loq);

        updatedJenisUji[idx] = {
            ...barisSaatIni,
            result: value,
            keterangan: ket,
        };

        setForm({ ...form, jenis_uji: updatedJenisUji });
    };

    const handleMethodeChange = (idx, value) => {
        const updatedJenisUji = [...form.jenis_uji];
        updatedJenisUji[idx] = {
            ...updatedJenisUji[idx],
            methode: value,
        };
        setForm({ ...form, jenis_uji: updatedJenisUji });
    };

    const handleLodChange = (idx, value) => {
        const updatedJenisUji = [...form.jenis_uji];
        const barisSaatIni = updatedJenisUji[idx];
        const ket = evaluateKeterangan(barisSaatIni.result, value);
        updatedJenisUji[idx] = {
            ...barisSaatIni,
            lod_loq: value,
            keterangan: ket,
        };
        setForm({ ...form, jenis_uji: updatedJenisUji });
    };

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const tambahBaris = () =>
        setForm((f) => ({ ...f, jenis_uji: [...f.jenis_uji, emptyBaris()] }));

    const hapusBaris = (idx) =>
        setForm((f) => ({ ...f, jenis_uji: f.jenis_uji.filter((_, i) => i !== idx) }));

    // Helper fungsi untuk generate random string sederhana jika tidak memakai library
    const generateRandomKey = () => {
        return 'LAB-' + Math.random().toString(36).substring(2, 11) + '-' + Date.now().toString(36);
    };

    const handleSubmit = async () => {
    // Validasi tambahan sebelum submit
    if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
        toast({
            title: "Ukuran file terlalu besar",
            description: "Ukuran dokumen maksimal 1 MB.",
            status: "error",
            duration: 4000,
            isClosable: true,
        });
        return;
    }

    setSaving(true);

        // 1. Generate unique key/random string untuk identifier file
        const fileKey = window.crypto?.randomUUID ? window.crypto.randomUUID() : generateRandomKey();

        let finalDokpath = "";

        try {
            // 2. STEP 1: Jika ada file, upload ke Servis File terlebih dahulu menggunakan fileKey
            if (selectedFile) {
                const formDataPayload = new FormData();
                formDataPayload.append("no_aju", fileKey); // Mengirim random string sebagai no_aju
                formDataPayload.append("file", selectedFile);

                const uploadRes = await LabUjiService.postFileUpload(formDataPayload);

                // Susun URL dokpath menggunakan fileKey
                finalDokpath = uploadRes?.data?.path || `${process.env.REACT_APP_API_FILE}/view/${fileKey}/${selectedFile.name}`;
            }

            // 3. STEP 2: Kirim data transaksi ke Servis Utama / Database
            const cleanJenisUji = form.jenis_uji.map((item) => ({
                id_uji: item.id_uji ? Number(item.id_uji) : null,
                kategori: item.kategori || "",
                nm_uji: item.nm_uji || "",
                result: item.result || "",
                satuan: item.satuan || "",
                methode: item.methode || "",
                lod_loq: item.lod_loq || "",
                keterangan: item.keterangan || "",
            }));

            const payload = {
                upi_id: Number(form.upi_id) || form.upi_id,
                nm_upi: form.nm_upi,
                nm_jenis_uji: form.nm_jenis_uji,
                tgl_contoh: form.tgl_contoh,
                tgl_sur: form.tgl_sur,
                tgl_lhu: form.tgl_lhu,
                tmp_uji: form.tmp_uji,
                created_by: form.created_by,
                dokpath: finalDokpath, // Dokpath sudah terisi URL/path valid!
                jenis_uji: cleanJenisUji,
            };

            await LabUjiService.createLabUji(payload);

            toast({ 
                title: "Data & Dokumen Berhasil Disimpan", 
                status: "success", 
                duration: 2500 
            });

            setSelectedFile(null);
            setForm(emptyForm());
            onCreated?.();
            onClose();
        } catch (err) {
            console.error("Gagal Submit Data:", err);
            toast({ 
                title: "Gagal Menyimpan Data", 
                description: err.response?.data?.message || err.message, 
                status: "error", 
                duration: 4000 
            });
        } finally {
            setSaving(false);
        }
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Tambah Data Pengujian</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontWeight="semibold" mb={2}>
                        Informasi UPI &amp; Contoh
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={5}>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Nama UPI</FormLabel>
                            <Input
                                size="sm"
                                placeholder="Ketik untuk cari UPI..."
                                mb={1.5}
                                value={searchUpi}
                                onChange={(e) => setSearchUpi(e.target.value)}
                            />
                            <Select
                                placeholder="Pilih UPI"
                                value={form.upi_id}
                                onChange={handleUpiChange}
                            >
                                {filteredUpiList.map((u) => (
                                    <option key={u.UPI_ID} value={u.UPI_ID}>
                                        {u.UPI_NAMA}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Nama Produk (Jenis Uji)</FormLabel>
                            <Input
                                size="sm"
                                placeholder="Ketik untuk cari produk..."
                                mb={1.5}
                                value={searchProduk}
                                onChange={(e) => setSearchProduk(e.target.value)}
                            />
                            <Select
                                placeholder="Pilih Produk"
                                onChange={handleProdukChange}
                            >
                                {filteredProdukList.map((pg) => (
                                    <option key={pg.id_produk} value={pg.id_produk}>
                                        {pg.nama_produk}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Tempat Uji</FormLabel>
                            <Input
                                placeholder="Contoh: Jakarta"
                                value={form.tmp_uji}
                                onChange={(e) => setField("tmp_uji", e.target.value)}
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Tgl Pengambilan Contoh</FormLabel>
                            <Input
                                type="date"
                                value={form.tgl_contoh}
                                onChange={(e) => setField("tgl_contoh", e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Tgl Surat / Terima Lab</FormLabel>
                            <Input
                                type="date"
                                value={form.tgl_sur}
                                onChange={(e) => setField("tgl_sur", e.target.value)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm">Tgl Terbit LHU</FormLabel>
                            <Input
                                type="date"
                                value={form.tgl_lhu}
                                onChange={(e) => setField("tgl_lhu", e.target.value)}
                            />
                        </FormControl>
                    </SimpleGrid>

                    <Divider mb={3} />

                    <Stack direction="row" justify="space-between" align="center" mb={2} flexWrap="wrap" gap={2}>
                        <Text fontWeight="semibold">Data Uji Lab</Text>
                        <HStack spacing={2}>
                            <Input
                                size="xs"
                                w="200px"
                                placeholder="Ketik untuk cari parameter..."
                                value={searchParam}
                                onChange={(e) => setSearchParam(e.target.value)}
                            />
                            <Button size="sm" leftIcon={<AddIcon boxSize={2} />} onClick={tambahBaris}>
                                Tambah Parameter
                            </Button>
                        </HStack>
                    </Stack>

                    <Stack spacing={2} mb={5}>
                        {form.jenis_uji.map((baris, idx) => (
                            <Box key={idx} borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                                <SimpleGrid columns={{ base: 1, md: 13 }} spacing={2} alignItems="flex-end">
                                    <FormControl isRequired gridColumn={{ base: "span 1", md: "span 3" }}>
                                        <FormLabel fontSize="xs">Parameter</FormLabel>
                                        <Select
                                            placeholder="Pilih parameter"
                                            size="sm"
                                            value={baris.id_uji}
                                            onChange={(e) => handleParamSelect(idx, e.target.value)}
                                            bg="white"
                                        >
                                            {filteredMasterUji.map((m) => (
                                                <option key={m.id_uji} value={m.id_uji}>
                                                    {m.kategori} - {m.parameter_uji}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                                        <FormLabel fontSize="xs">Hasil Uji</FormLabel>
                                        <Input
                                            size="sm"
                                            value={baris.result || ""}
                                            onChange={(e) => handleHasilChange(idx, e.target.value)}
                                            bg="white"
                                        />
                                    </FormControl>

                                    <FormControl gridColumn={{ base: "span 1", md: "span 1" }}>
                                        <FormLabel fontSize="xs">Satuan</FormLabel>
                                        <Input
                                            size="sm"
                                            value={baris.satuan || ""}
                                            isReadOnly
                                            bg="gray.100"
                                        />
                                    </FormControl>

                                    <FormControl gridColumn={{ base: "span 2", md: "span 3" }}>
                                        <FormLabel fontSize="xs">Methode</FormLabel>
                                        <Input
                                            size="sm"
                                            value={baris.methode || ""}
                                            onChange={(e) => handleMethodeChange(idx, e.target.value)}
                                            bg="white"
                                            placeholder="Metode uji..."
                                        />
                                    </FormControl>

                                    <FormControl gridColumn={{ base: "span 1", md: "span 1" }}>
                                        <FormLabel fontSize="xs">LOD/LOQ</FormLabel>
                                        <Input
                                            size="sm"
                                            value={baris.lod_loq || ""}
                                            onChange={(e) => handleLodChange(idx, e.target.value)}
                                            bg="white"
                                            placeholder="LOD/LOQ..."
                                        />
                                    </FormControl>

                                    <FormControl gridColumn={{ base: "span 1", md: "span 2" }}>
                                        <FormLabel fontSize="xs">Keterangan</FormLabel>
                                        <Input
                                            size="sm"
                                            value={baris.keterangan || ""}
                                            isReadOnly
                                            fontWeight="bold"
                                            color={
                                                baris.keterangan === "Sesuai Standar"
                                                    ? "green.600"
                                                    : "red.600"
                                            }
                                            bg="gray.100"
                                            placeholder="Otomatis"
                                        />
                                    </FormControl>

                                    <Box 
                                        gridColumn={{ base: "span 1", md: "span 1" }} 
                                        display="flex" 
                                        justifyContent="center" 
                                        alignItems="center"
                                    >
                                        <IconButton
                                            aria-label="Hapus baris"
                                            icon={<DeleteIcon />}
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            isDisabled={form.jenis_uji.length === 1}
                                            onClick={() => hapusBaris(idx)}
                                        />
                                    </Box>
                                </SimpleGrid>
                            </Box>
                        ))}
                    </Stack>

                    <FormControl mb={3}>
                        <FormLabel fontSize="sm">
                            Upload Dokumen
                        </FormLabel>

                        <Input
                            type="file"
                            p={1}
                            onChange={handleFileSelect}
                        />

                        <Text fontSize="xs" color="gray.500" mt={1}>
                            Maksimal ukuran file 1 MB
                        </Text>

                        {selectedFile && (
                            <Text fontSize="xs" color="green.600" mt={1}>
                                File terpilih: {selectedFile.name} (
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </Text>
                        )}
                    </FormControl>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Batal
                    </Button>
                    <Button 
                        colorScheme="blue" 
                        onClick={handleSubmit} 
                        isLoading={saving}
                    >
                        Simpan
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}